import { render } from 'preact-render-to-string';
import {
  Line,
  BasicEmailWrapper,
  MarketingFooter,
  PrimaryAction,
  Heading,
  SubHeading,
} from './shared';
import { Tenant } from 'server/types';
import { tenantIntl } from 'shared/intl/use-intl.server';
import { OTP_EXPIRATION_MINUTES } from 'shared/consts';

export function ForgotPassword({
  tenant,
  resetCode,
  returnURL,
}: {
  tenant: Tenant;
  resetCode: string;
  returnURL?: string;
}) {
  const signinUrl = `https://${tenant.domain}/one-click-login?code=${resetCode}&redirect=${encodeURIComponent(returnURL || '/')}`;
  const intl = tenantIntl(tenant);

  return render(
    <BasicEmailWrapper footer={<MarketingFooter />}>
      <Heading>
        {intl('Sign in to {tenantName:string}', {
          tenantName: tenant.name,
        })}
      </Heading>
      <Line>
        {intl(
          `Looks like you've requested a link to sign in to {tenantName:string}. Just click the link below and you'll be securely signed in.`,
          {
            tenantName: tenant.name,
          },
        )}
      </Line>
      <PrimaryAction href={signinUrl}>{intl('One-click sign in')}</PrimaryAction>
      <Line>
        {intl(`If that's not working, copy and paste this link into your browser's address bar:`)}
      </Line>
      <Line>
        <a
          href={signinUrl}
          target="_blank"
          style="word-wrap:break-word;word-break:break-all;"
          rel="noreferrer"
        >
          {signinUrl}
        </a>
      </Line>
      <Line>{intl(`If you didn't request this link, you can ignore this email.`)}</Line>
    </BasicEmailWrapper>,
  );
}

export function CalendarContinueSignup({
  tenant,
  resetCode,
  mode,
}: {
  tenant: Tenant;
  resetCode: string;
  mode: 'modal' | 'signup';
}) {
  const pathname =
    mode === 'signup'
      ? `/calendar/signup?code=${resetCode}`
      : `/interview/preview?modal=confirmed&code=${resetCode}`;
  const signinUrl = `https://${tenant.domain}${pathname}`;
  const intl = tenantIntl(tenant);

  return render(
    <BasicEmailWrapper>
      <Heading>Your new page is ready!</Heading>
      <Line>
        Great work - you've gone from "here's an idea" to a complete coaching page. Check it out -
        you'll see how we've captured your unique approach and the transformation you create for
        clients. Plus, you can customize everything to make it your own.
      </Line>
      <PrimaryAction href={signinUrl}>{intl('View Your New Page →')}</PrimaryAction>
      <SubHeading>📣 Questions? Just hit reply.</SubHeading>
      <Line>
        Need help customizing your page, connecting your calendar, or figuring out next steps? Reply
        to this email, and we'll personally help you get everything set up perfectly.
      </Line>
      <Line>{intl(`If you didn't request this link, you can ignore this email.`)}</Line>
    </BasicEmailWrapper>,
  );
}

export function CalendarForgotPassword({
  tenant,
  resetCode,
}: {
  tenant: Tenant;
  resetCode: string;
}) {
  const signinUrl = `https://${tenant.domain}/calendar/login?code=${resetCode}`;
  const intl = tenantIntl(tenant);

  return render(
    <BasicEmailWrapper>
      <Heading>Sign in to your calendar</Heading>
      <Line>Click the link below and you'll be securely signed in to your calendar.</Line>
      <PrimaryAction href={signinUrl}>{intl('Sign in to calendar')}</PrimaryAction>
      <Line>
        {intl(`If that's not working, copy and paste this link into your browser's address bar:`)}
      </Line>
      <Line>
        <a
          href={signinUrl}
          target="_blank"
          style="word-wrap:break-word;word-break:break-all;"
          rel="noreferrer"
        >
          {signinUrl}
        </a>
      </Line>
      <Line>{intl(`If you didn't request this link, you can ignore this email.`)}</Line>
    </BasicEmailWrapper>,
  );
}

export function OtpEmailTemplate({ otp }: { otp: string }) {
  return render(
    <BasicEmailWrapper footer={<MarketingFooter />}>
      <SubHeading>Your login code</SubHeading>
      <Line>
        Please return to the login page and enter the code below to sign in. This code will expire
        in {OTP_EXPIRATION_MINUTES} minutes and can only be used once.
      </Line>
      <Heading>{otp}</Heading>
      <Line>If you didn't request this code, you can ignore this email.</Line>
    </BasicEmailWrapper>,
  );
}

export function EmailVerification({
  tenant,
  resetCode,
  returnURL,
}: {
  tenant: Tenant;
  resetCode: string;
  returnURL?: string;
}) {
  const signinUrl = `https://${tenant.domain}/verify-email?code=${resetCode}&redirect=${encodeURIComponent(returnURL || '/')}`;
  const intl = tenantIntl(tenant);

  return render(
    <BasicEmailWrapper footer={<MarketingFooter />}>
      <Heading>Verify Your Email Address</Heading>
      <Line>
        To keep your account secure and ensure you can send messages to your students, please verify
        your email address by clicking the button below.
      </Line>
      <PrimaryAction href={signinUrl}>{intl('Verify Email Address')}</PrimaryAction>
      <Line>
        {intl(`If that's not working, copy and paste this link into your browser's address bar:`)}
      </Line>
      <Line>
        <a
          href={signinUrl}
          target="_blank"
          style="word-wrap:break-word;word-break:break-all;"
          rel="noreferrer"
        >
          {signinUrl}
        </a>
      </Line>
      <Line>{intl(`If you didn't request this link, you can ignore this email.`)}</Line>
    </BasicEmailWrapper>,
  );
}
